// Typ: FindAreaHeronTask
// Wzór Herona: P = sqrt( p (p-a) (p-b) (p-c) ),  p = (a+b+c)/2

import { InlineMath, BlockMath } from "react-katex";
import RootFractionInput, {
  parseRootFractionInput,
  formatLatexPreview
} from "../../../../components/inputs/RootFractionInput";

// ===== utils =====
const rnd = (a, b) => Math.floor(Math.random() * (b - a + 1)) + a;

function gcd(a, b) {
  a = Math.abs(a); b = Math.abs(b);
  while (b) [a, b] = [b, a % b];
  return a || 1;
}

// n = out^2 * inside, inside square-free
function simplifySqrtInt(n) {
  if (n <= 0) return { outside: 0, inside: 1 };
  let outside = 1;
  let inside = n;
  for (let i = 2; i * i <= inside; i++) {
    let sq = i * i;
    while (inside % sq === 0) {
      outside *= i;
      inside /= sq;
    }
  }
  return { outside, inside };
}

// ładny zapis współczynnika * sqrt(root) / den
// gdy root == 1 → ładna liczba (całkowita / ułamek właściwy / mieszana)
function formatCoeffWithRoot(coeff, den, root) {
  const d = gcd(Math.abs(coeff), Math.abs(den));
  let num = coeff / d;
  let denS = den / d;

  if (!root || root === 1) {
    const sign = num < 0 ? "-" : "";
    const absNum = Math.abs(num);

    if (denS === 1) return `${num}`; // całkowita
    if (absNum < denS) return `${sign}\\dfrac{${absNum}}{${denS}}`; // właściwy

    const whole = Math.floor(absNum / denS);
    const rem = absNum % denS;
    if (rem === 0) return `${sign}${whole}`;
    return `${sign}${whole}\\dfrac{${rem}}{${denS}}`; // mieszana
  }

  const sign = num < 0 ? "-" : "";
  const nabs = Math.abs(num);

  if (nabs === 1 && denS === 1) return `${sign}\\sqrt{${root}}`;
  if (denS === 1) return `${sign}${nabs}\\sqrt{${root}}`;
  if (nabs === 1) return `${sign}\\dfrac{\\sqrt{${root}}}{${denS}}`;
  return `${sign}\\dfrac{${nabs}\\sqrt{${root}}}{${denS}}`;
}

// ===== generator =====
export function generateTask() {
  // preferencje:
  // - najczęściej: 1 parzysty + 2 nieparzyste (często daje P całkowite lub "ładne")
  // - czasem: wszystkie parzyste (zawsze brak "połówek")
  const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];
  const rndEven = (min, max) => {
    let x;
    do x = rnd(min, max);
    while (x % 2 !== 0);
    return x;
  };
  const rndOdd = (min, max) => {
    let x;
    do x = rnd(min, max);
    while (x % 2 === 0);
    return x;
  };

  // ocenianie "ładności" wyniku po uproszczeniu:
  // - najlepsze: inside == 1 (pole bez pierwiastka)
  // - potem: mały inside (np. 2,3,5,6,7,10...)
  // - potem: cokolwiek (byle bez ułamków)
  function scoreTriplet(a, b, c) {
    // warunek trójkąta
    if (!(a + b > c && a + c > b && b + c > a)) return null;

    const t1 = a + b + c;
    const t2 = -a + b + c;
    const t3 = a - b + c;
    const t4 = a + b - c;
    const prod = t1 * t2 * t3 * t4;

    const { outside, inside } = simplifySqrtInt(prod);

    // Po uproszczeniu wynik to outside*sqrt(inside)/4.
    // Chcemy, żeby "4" się skróciło całkowicie -> outside podzielne przez 4.
    // (Przy naszych parzystościach zwykle będzie, ale pilnujemy na pewno.)
    if (outside % 4 !== 0) return null;

    // Im mniejsze inside, tym lepiej.
    // inside==1 => pole całkowite, mega.
    let s = 0;
    if (inside === 1) s += 100000;        // najwyższy priorytet
    s += Math.max(0, 2000 - inside);      // preferuj mniejsze pierwiastki
    s += outside;                         // delikatnie preferuj większy współczynnik (czytelniej niż sqrt(dużej))
    return { score: s, outside, inside };
  }

  const MIN = 6;
  const MAX = 24;

  // Spróbujmy wylosować sporo kandydatów i wybierzmy najlepszego.
  // To działa szybko, bo zakres jest mały.
  let best = null;
  let bestABC = null;

  for (let tries = 0; tries < 200; tries++) {
    const mode = Math.random() < 0.7 ? "one_even_two_odd" : "all_even";

    let a, b, c;

    if (mode === "all_even") {
      a = rndEven(MIN, MAX);
      b = rndEven(MIN, MAX);
      c = rndEven(MIN, MAX);
    } else {
      // 1 parzysty, 2 nieparzyste
      const evenPos = pick([0, 1, 2]);
      const vals = [null, null, null];
      for (let i = 0; i < 3; i++) {
        vals[i] = (i === evenPos) ? rndEven(MIN, MAX) : rndOdd(MIN, MAX);
      }
      [a, b, c] = vals;
    }

    // żeby nie wchodziły "prawie-degeneraty", które dają brzydkie liczby,
    // możemy lekko dociąć przypadki bardzo bliskie nierówności:
    // (opcjonalne, ale pomaga na estetykę)
    const sorted = [a, b, c].sort((p, q) => p - q);
    if (sorted[0] + sorted[1] <= sorted[2] + 1) continue; // odetnij "prawie równość"

    const sc = scoreTriplet(a, b, c);
    if (!sc) continue;

    if (!best || sc.score > best.score) {
      best = sc;
      bestABC = { a, b, c };
      // jeśli trafimy ideał (inside==1), możemy szybciej zakończyć
      if (sc.inside === 1) break;
    }
  }

  // fallback: gdyby ekstremalnie nie znalazło (mało prawdopodobne),
  // wracamy do prostego losowania jak wcześniej
  if (!bestABC) {
    let a, b, c;
    do {
      a = rnd(MIN, MAX);
      b = rnd(MIN, MAX);
      c = rnd(MIN, MAX);
    } while (!(a + b > c && a + c > b && b + c > a));
    return { type: "FindAreaHeronTask", data: { a, b, c } };
  }

  return {
    type: "FindAreaHeronTask",
    data: bestABC
  };
}


// ===== render task =====
export function renderTask({ data }) {
  const { a, b, c } = data;
  return (
    <div style={{ marginBottom: 8 }}>
      W trójkącie <InlineMath math={'ABC'} /> dane są boki
      <InlineMath math={`\\;a=${a}`} />,{" "}
      <InlineMath math={`\\;b=${b}`} />,{" "}
      <InlineMath math={`\\;c=${c}`} />.
      Oblicz pole trójkąta <InlineMath math={'P'} />.
    </div>
  );
}

// ===== input =====
export function renderInput(_, userAnswer, onChange, disabled) {
  return (
    <div style={{ display: 'grid', gap: 10, marginTop: 8 }}>
      <RootFractionInput
        value={userAnswer}
        onChange={onChange}
        disabled={disabled}
        label={'P'}
        placeholder="np. (15√3)/2, 12, 7 1/2"
      />
    </div>
  );
}

// ===== check =====
export function checkAnswer({ data }, userAnswer) {
  const { a, b, c } = data;

  // Heron w wersji „całkowitej”: P = sqrt( (a+b+c)(-a+b+c)(a-b+c)(a+b-c) ) / 4
  const t1 = a + b + c;
  const t2 = -a + b + c;
  const t3 = a - b + c;
  const t4 = a + b - c;
  const prod = t1 * t2 * t3 * t4;
  const expected = Math.sqrt(prod) / 4;

  // 1) standardowy parse z RootFractionInput
  const raw = typeof userAnswer === 'object' ? userAnswer.raw : userAnswer;
  let parsed =
    typeof userAnswer === 'object' && userAnswer.parsed != null
      ? userAnswer.parsed
      : parseRootFractionInput(raw);

  // 2) fallback: liczba z przecinkiem
  if ((parsed == null || !Number.isFinite(parsed)) && typeof raw === 'string') {
    const asNumber = Number(raw.replace(',', '.'));
    if (Number.isFinite(asNumber)) parsed = asNumber;
  }

  if (parsed == null || !Number.isFinite(parsed)) return false;

  // akceptuj zaokrąglenie do setnych
  return Math.abs(parsed - expected) <= 0.01;
}

// ===== answer =====
export function renderAnswer({ data }) {
  const { a, b, c } = data;

  const p = (a + b + c) / 2;
  const pa = p - a;
  const pb = p - b;
  const pc = p - c;

  // Jeśli p jest całkowite (u Ciebie teraz praktycznie zawsze) → ładny Heron bez /4
  if (Number.isInteger(p) && Number.isInteger(pa) && Number.isInteger(pb) && Number.isInteger(pc)) {
    const prod = p * pa * pb * pc;
    const { outside, inside } = simplifySqrtInt(prod);

    // den = 1, bo nie ma żadnego /4
    const latex = formatCoeffWithRoot(outside, 1, inside);

    return (
      <div style={{ marginTop: 6 }}>
        <InlineMath math={`P = ${latex}`} />
      </div>
    );
  }

  // Fallback (gdyby kiedyś wróciły połówki)
  const t1 = a + b + c;
  const t2 = -a + b + c;
  const t3 = a - b + c;
  const t4 = a + b - c;
  const prod = t1 * t2 * t3 * t4;

  const { outside, inside } = simplifySqrtInt(prod);
  const latex = formatCoeffWithRoot(outside, 4, inside);

  return (
    <div style={{ marginTop: 6 }}>
      <InlineMath math={`P = ${latex}`} />
    </div>
  );
}


// ===== explanation =====
export function renderExplanation({ data }) {
  const { a, b, c } = data;

  const p = (a + b + c) / 2;
  const pa = p - a;
  const pb = p - b;
  const pc = p - c;

  const isNice =
    Number.isInteger(p) && Number.isInteger(pa) && Number.isInteger(pb) && Number.isInteger(pc);

  if (isNice) {
    const prod = p * pa * pb * pc;

    const { outside, inside } = simplifySqrtInt(prod);
    const simplified = formatCoeffWithRoot(outside, 1, inside);
    const changed = inside !== prod || outside !== 1;

    return (
      <div style={{ textAlign: "left", lineHeight: 1.7 }}>
        <div><strong>Krok 1.</strong> Wzór Herona:</div>
        <BlockMath math={'P = \\sqrt{p\\,(p-a)\\,(p-b)\\,(p-c)},\\quad p=\\dfrac{a+b+c}{2}'} />

        <div><strong>Krok 2.</strong> Liczymy półobwód i różnice:</div>
        <BlockMath math={`p = \\dfrac{${a}+${b}+${c}}{2} = ${p}`} />
        <BlockMath math={`p-a = ${p}-${a} = ${pa}`} />
        <BlockMath math={`p-b = ${p}-${b} = ${pb}`} />
        <BlockMath math={`p-c = ${p}-${c} = ${pc}`} />

        <div><strong>Krok 3.</strong> Podstawiamy do wzoru:</div>
        <BlockMath math={`P = \\sqrt{${p}\\cdot ${pa}\\cdot ${pb}\\cdot ${pc}}`} />

        <div><strong>Krok 4.</strong> Upraszczamy pierwiastek:</div>
        <BlockMath math={`P = \\sqrt{${prod}}`} />
        {changed ? <BlockMath math={`P = ${simplified}`} /> : null}
      </div>
    );
  }

  // Fallback (gdyby kiedyś wróciły połówki)
  const prodInteger = (a + b + c) * (-a + b + c) * (a - b + c) * (a + b - c);
  const { outside, inside } = simplifySqrtInt(prodInteger);
  const simplified = formatCoeffWithRoot(outside, 4, inside);

  const g = gcd(outside, 4);
  const changed = inside !== prodInteger || g > 1;

  return (
    <div style={{ textAlign: "left", lineHeight: 1.7 }}>
      <div><strong>Krok 1.</strong> Wzór Herona:</div>
      <BlockMath math={'P = \\sqrt{p\\,(p-a)\\,(p-b)\\,(p-c)},\\quad p=\\dfrac{a+b+c}{2}'} />

      <div><strong>Krok 2.</strong> Liczymy półobwód i różnice:</div>
      <BlockMath math={`p = \\dfrac{${a}+${b}+${c}}{2} = ${p}`} />
      <BlockMath math={`p-a = ${p}-${a} = ${pa}`} />
      <BlockMath math={`p-b = ${p}-${b} = ${pb}`} />
      <BlockMath math={`p-c = ${p}-${c} = ${pc}`} />

      <div><strong>Krok 3.</strong> Podstawiamy do wzoru:</div>
      <BlockMath math={`P = \\sqrt{${p}\\cdot ${pa}\\cdot ${pb}\\cdot ${pc}}`} />

      <div><strong>Krok 4.</strong> Porządkujemy mianownik i upraszczamy pierwiastek:</div>
      <BlockMath math={`P = \\dfrac{\\sqrt{${prodInteger}}}{4}`} />
      {changed ? <BlockMath math={`P = ${simplified}`} /> : null}
    </div>
  );
}


// ===== user answer =====
export function renderUserAnswer(userAnswer) {
  const raw = typeof userAnswer === 'object' ? userAnswer.raw : userAnswer;
  if (!raw || typeof raw !== 'string' || raw.trim() === '') {
    return <i>(brak odpowiedzi)</i>;
  }
  return (
    <div style={{ marginTop: 8 }}>
      <InlineMath math={formatLatexPreview(raw, 'P')} />
    </div>
  );
}
